import React, { useEffect, useState } from 'react';
import { useStateSelector } from '../../../../store/selectors';
import BootstrapTable, {
    TableChangeState,
    TableColumnFilterProps,
} from '../../../../components/react-bootstrap-table-next/react-bootstrap-table2';
import filterFactory, {
    FILTER_TYPES,
    selectFilter,
} from '../../../../components/react-bootstrap-table-next/react-bootstrap-table2-filter';
import paginationFactory from '../../../../components/react-bootstrap-table-next/react-bootstrap-table2-paginator';
import { OpportunitiesTableFilter } from '../../../../components/Table/models/table-filter';
import createNotification from '../../../../utils/createNotification';
import moment from 'moment';
import './MarketplaceOpportunities.scss';
import { Button, Col } from 'reactstrap';
import ConfirmationDialogWithReason from '../../../../components/ConfirmationDialog/confirmation-dialog-with-reason';
import { MarketplaceOpportunity } from '../../../../slices/marketplace/opportunity/models/opportunity';
import {
    MarketplaceOpportunityStatus,
    MarketplaceOpportunityStatusList,
    MarketplaceOpportunityStatusOptions,
} from '../../../../slices/marketplace/opportunity/models/opportunity-status';
import { GetOpportunities } from '../../../../slices/marketplace/opportunity/thunks/get-opportunities.thunk';
import { useDispatch } from 'react-redux';
import { setSelectedOpportunity } from '../../../../slices/marketplace/opportunity/opportunity.slice';
import { TimelineEvent } from '../../../../slices/marketplace/timeline/models/timeline-event.model';
import { GetBuyer } from '../../../../slices/marketplace/buyer/thunks/get-buyer.thunk';
import LandscapeMapContainer, {
    Region,
    regionConfigs,
} from '../../../LandscapeMapContainer/LandscapeMapContainer';
import { MarkerData } from '../../../../components/LandscapeMap/LandscapeMapTypes';
import { ShapeType } from '../../../../components/LandscapeMap/Markers/Shapes/MarkersWrapper';
import { zipCodeConvert } from '../../../../utils/zipcodeConvertor/zipcodeConvertor';
import { OpportunityLocation } from '../../../../slices/marketplace/opportunity/models/opportunity-location';
import {
    PracticeFocus,
    PracticeFocusOptionsMapping,
    PracticeFocusOptionsShort,
} from '../../../../utils/constants/PracticeFocus.enum';
import { useSearchParams } from 'react-router-dom';
import {
    getAllStates,
    stateCodeToNameMapper,
    stateToCodeMapper,
} from '../../../../utils/constants';
import InfoTooltip from '../../../../components/info-tooltip/info-tooltip';
import {
    LocationTypeOptions,
    LocationTypes,
} from '../../../../slices/marketplace/timeline/models/enums/location-type.enum';
import ReactDOMServer from 'react-dom/server';
import { SaveSelectedOpportunity } from '../../../../slices/marketplace/buyer/thunks/save-selected-opportunity.thunk';
import { MarketplaceBuyer } from '../../../../slices/marketplace/buyer/models/buyer';

interface MarketplaceOpportunitiesProps {
    refreshOpportunitiesToggle: boolean;
}
const MarketplaceOpportunities = (props: MarketplaceOpportunitiesProps) => {
    const [searchParams] = useSearchParams({});
    const opportunityId = searchParams.get('opportunityId');
    const [page, setPage] = useState(1);
    const [totalSize, setTotalSize] = useState(1);
    const [sizePerPage, setSizePerPage] = useState(5);
    const [opportunities, setOpportunities] = useState<
        MarketplaceOpportunity[]
    >([]);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [markers, setMarkers] = useState<MarkerData[]>([]);
    const [selectedMarkers, setSelectedMarkers] = useState<MarkerData[]>([]);
    const [opportunitiesLocations, setOpportunitiesLocations] = useState<
        OpportunityLocation[]
    >([]);
    const axios = useStateSelector((state) => state.core.axios);
    const [isConfirmationVisible, setIsConfirmationVisible] = useState(false);
    const [reasonForRejection, setReasonForRejection] = useState('');
    const [selectedOpportunityIdToReject, setSelectedOpportunityIdToReject] =
        useState(0);
    const [selectedOpportunityIdFromMap, setSelectedOpportunityIdFromMap] =
        useState(0);
    const [rejectedPracticeName, setRejectedPracticeName] = useState('');
    const [allStates, setAllStates] = useState([]);

    const engagingAvailableStatuses = [
        MarketplaceOpportunityStatus.RejectedByBuyer,
        MarketplaceOpportunityStatus.NewOpportunity,
        MarketplaceOpportunityStatus.Tagged,
    ];

    const taggingAvailableStatuses = [
        MarketplaceOpportunityStatus.RejectedByBuyer,
        MarketplaceOpportunityStatus.NewOpportunity,
    ];

    const rejectingAvailableStatuses = [
        MarketplaceOpportunityStatus.Tagged,
        MarketplaceOpportunityStatus.NewOpportunity,
        MarketplaceOpportunityStatus.Engaged,
        MarketplaceOpportunityStatus.BuyerMissedDeadline,
        MarketplaceOpportunityStatus.AwaitingResponse,
    ];

    const defaultSorted: [{ dataField: any; order: any }] = [
        {
            dataField: 'listingDate',
            order: 'asc',
        },
    ];
    const [currentSort, setCurrentSort] = useState<OpportunitiesTableFilter>(
        new OpportunitiesTableFilter(
            { filters: [], logic: '' },
            page,
            sizePerPage,
            {
                dir: 'asc',
                field: 'listingDate',
            },
            null
        )
    );
    const [currentZoom, setCurrentZoom] = useState(
        regionConfigs[Region.Mainland].defaultZoom
    );
    const dispatch = useDispatch() as any;
    const opportunitiesTable = useStateSelector(
        (state) => state.opportunities.opportunities
    );

    const handleTableChange = (
        _type: any,
        newState: TableChangeState<MarketplaceOpportunity>
    ) => {
        const { page, sizePerPage, sortField, sortOrder } = newState;

        const nestedField = nestedFields.find((nf) => nf.name == sortField);
        const nestedPropPath = nestedField?.sortPath ?? sortField;

        const updatedPostObj = new OpportunitiesTableFilter(
            { filters: [], logic: '' },
            page,
            sizePerPage,
            {
                dir: sortOrder,
                field: nestedPropPath
                    ? nestedPropPath
                    : defaultSorted[0].dataField,
            },
            null
        );

        const arrayOfObj = Object.entries(newState.filters).map((e) => ({
            [e[0]]: e[1],
        }));

        if (arrayOfObj.length > 0) {
            arrayOfObj.forEach((element) => {
                let key = Object.keys(element);

                let values = newState.filters[key.toString()];

                if (
                    values.filterType != FILTER_TYPES.DATE ||
                    values.filterVal.date != null
                ) {
                    const nestedField = nestedFields.find(
                        (nf) => nf.name == key[0]
                    );
                    const field = nestedField?.filterPath ?? key[0];

                    let filterOperator =
                        values.filterType == FILTER_TYPES.DATE ||
                        values.filterType == FILTER_TYPES.SELECT
                            ? 'eq'
                            : 'contains';

                    if (nestedField?.operator) {
                        filterOperator = nestedField.operator;
                    }

                    let obj = {
                        Field: field,
                        Operator: filterOperator,
                        Value:
                            values.filterType == FILTER_TYPES.DATE
                                ? moment(values.filterVal.date).format(
                                      'MM.DD.YYYY'
                                  )
                                : values.filterVal,
                        NextFilterOperator:
                            nestedField?.name ===
                            'practices.stateOfRegistration'
                                ? 'or'
                                : 'eq',
                    };

                    updatedPostObj.filter.filters.push(obj);

                    if (nestedField?.name === 'practices.stateOfRegistration') {
                        const stateNameFilter = {
                            Field: field,
                            Operator: filterOperator,
                            Value: stateCodeToNameMapper(values.filterVal),
                        };

                        updatedPostObj.filter.filters.push(stateNameFilter);
                    }
                }
            });
        }

        if (
            updatedPostObj.filter.filters.filter(
                (f) => f.filterPath === 'practices.location'
            ).length === 1
        ) {
            updatedPostObj.filter.filters =
                updatedPostObj.filter.filters.filter(
                    (f) => f.Field !== 'practices.location'
                );
        }

        setCurrentSort(updatedPostObj);
        fetchData(updatedPostObj);
    };

    const fetchData = async (postObj: OpportunitiesTableFilter) => {
        GetOpportunitiesLocations();
        return await dispatch(GetOpportunities(postObj));
    };

    const highlightOpportunityMarkers = (
        opportunityId: number,
        markersData: MarkerData[]
    ) => {
        const opportunityLocation = opportunitiesLocations.find(
            (x) => x.opportunityId == opportunityId
        );
        const practiceIds = opportunityLocation?.practices.map(
            (x) => x.practiceId
        );

        const newSelectedMarkers = markersData.filter((x) =>
            practiceIds?.includes(x.id)
        );
        setSelectedMarkers(newSelectedMarkers);
    };

    const handleTableRowSelect = (
        opportunityId: number,
        markersData: MarkerData[]
    ) => {
        setSelectedOpportunityIdFromMap(opportunityId);
        const opportunity = opportunitiesTable.opportunities.find(
            (x) => x.opportunityId == opportunityId
        );

        dispatch(SaveSelectedOpportunity(opportunityId));

        if (opportunity == null) {
            fetchData({
                ...currentSort,
                selectedOpportunityId: opportunityId.toString(),
            });
        } else {
            dispatch(setSelectedOpportunity(opportunityId));
        }

        highlightOpportunityMarkers(opportunityId, markersData);
    };

    const selectedOpportunity = useStateSelector(
        (state) => state.opportunities.selectedOpportuity
    );

    const getStates = () => {
        axios
            .get('api/location/StatesByRegion')
            .then((response) => {
                if (response && response.data && response.data.data) {
                    const allStates = getAllStates(response.data.data);

                    setAllStates(allStates);
                } else {
                    createNotification(
                        'An unexpected error occurred while loading Location Data.',
                        'error'
                    );
                }
            })
            .catch(() => {
                createNotification(
                    'An unexpected error occurred while loading Location Data.',
                    'error'
                );
            });
    };

    useEffect(() => {
        const opportunitiesDeepCopy = JSON.parse(
            JSON.stringify(opportunitiesTable.opportunities)
        );
        setOpportunities(opportunitiesDeepCopy);
        setPage(opportunitiesTable.pageNumber);
        setTotalSize(opportunitiesTable.totalItems);
        setSizePerPage(opportunitiesTable.pageSize);
        if (selectedOpportunityIdFromMap !== 0) {
            dispatch(setSelectedOpportunity(selectedOpportunityIdFromMap));
        }
        mapMarkers();
    }, [opportunitiesTable, allStates]);

    useEffect(() => {
        getStates();
        fetchProfile().then((buyer) => {
            const initialOpportunityId =
                opportunityId ?? buyer.selectedOpportunityId?.toString() ?? '';

            fetchData({
                ...currentSort,
                selectedOpportunityId: initialOpportunityId,
            }).then(() => {
                if (initialOpportunityId) {
                    setSelectedOpportunityIdFromMap(
                        Number(initialOpportunityId)
                    );
                    dispatch(
                        setSelectedOpportunity(Number(initialOpportunityId))
                    );
                }
            });
        });
    }, []);

    useEffect(() => {
        fetchData({
            ...currentSort,
            selectedOpportunityId: opportunityId ?? null,
        });
    }, [props.refreshOpportunitiesToggle]);

    useEffect(() => {
        if (
            opportunityId &&
            selectedOpportunity &&
            markers?.length > 0 &&
            opportunitiesLocations?.length > 0 &&
            opportunitiesTable?.opportunities?.length > 0
        ) {
            highlightOpportunityMarkers(
                selectedOpportunity.opportunityId,
                markers
            );
        }
    }, [selectedOpportunity, markers]);

    const mapMarkers = () => {
        const practices = opportunitiesLocations.flatMap((x) => x.practices);
        const newMarkers: MarkerData[] = practices.map((practice) => {
            const position = getMarkerPosition(practice.zipCode);
            const opportunity = opportunitiesLocations.find((x) =>
                x.practices.some((x) => x.practiceId == practice.practiceId)
            );
            return {
                id: practice.practiceId,
                name: practice.practiceName,
                position: position,
                color: getMarkerColor(opportunity.status),
                shape: getShapeByPracticeType(practice.practiceType),
                fteDVMs: practice.fteDvms,
                isIdNdaSigned: practice.isIdNdaSigned,
                showTooltip: false,
                onClick: (_: any) => {
                    handleTableRowSelect(opportunity.opportunityId, newMarkers);
                },
            };
        });
        setMarkers(() => newMarkers);
    };

    const getMarkerColor = (status: MarketplaceOpportunityStatus) => {
        const config = MarketplaceOpportunityStatusList[status];
        return config.color;
    };

    const getMarkerPosition = (zipCode: string): [number, number] | null => {
        try {
            return zipCodeConvert(zipCode);
        } catch {
            return [0, 0];
        }
    };

    const getShapeByPracticeType = (type: number): ShapeType => {
        switch (type) {
            case PracticeFocus.GP:
                return ShapeType.Circle;
            case PracticeFocus.UrgentCare:
                return ShapeType.Pentagon;
            case PracticeFocus.ER:
                return ShapeType.Octagon;
            case PracticeFocus.Specialty:
                return ShapeType.Diamond;
            case PracticeFocus.InHomeEuthanasia:
                return ShapeType.Star6;
            case PracticeFocus.Hybrid:
                return ShapeType.Triangle;
            case PracticeFocus.Equine:
                return ShapeType.Star5;
            case PracticeFocus.SpecER:
                return ShapeType.Rectangle;
            case PracticeFocus.Other:
                return ShapeType.Star4;
            default:
                return ShapeType.Circle;
        }
    };

    const fetchProfile = async (): Promise<MarketplaceBuyer> => {
        const result = await dispatch(GetBuyer(null));
        const buyer = result.payload.buyer as MarketplaceBuyer;
        return buyer;
    };

    const renderColorsDescription = () => {
        return Object.entries(MarketplaceOpportunityStatusList).map((type) => {
            const config = type[1];
            return (
                <div className="legend-item">
                    <div
                        className="item-bullet"
                        style={{
                            background: config.color,
                        }}></div>
                    <div className="item-name">{config.name}</div>
                </div>
            );
        });
    };

    const renderShapesDescription = () => {
        return PracticeFocusOptionsMapping.map((practiceFocus) => {
            const name = practiceFocus.label;
            const icon = practiceFocus.mapMarker;
            if (name === 'GP') {
                return (
                    <div className="legend-item">
                        <div
                            className="item-bullet"
                            style={{
                                background: '#7d7d7d',
                            }}></div>
                        <div className="item-name">{name}</div>
                    </div>
                );
            }
            return (
                <div className="legend-item">
                    <div className="item-bullet">{icon}</div>
                    <div className="item-name">{name}</div>
                </div>
            );
        });
    };

    const GetOpportunitiesLocations = () => {
        axios
            .get(
                `/api/Marketplace/MarketplaceOpportunities/GetOpportunitiesLocations`
            )
            .then((response) => {
                if (response?.request?.status !== 200) {
                    createNotification(
                        'An Error occured during fetching locations.',
                        'error'
                    );
                } else {
                    setOpportunitiesLocations(response.data);
                }
            });
    };

    const engage = (opportunityId: number) => {
        axios
            .post(
                `/api/Marketplace/MarketplaceOpportunities/${opportunityId}/Engage`
            )
            .then((response) => {
                if (response?.request?.status !== 200) {
                    createNotification(
                        'An Error occured during engaging.',
                        'error'
                    );
                } else {
                    fetchData(currentSort).then(() => {
                        dispatch(SaveSelectedOpportunity(opportunityId));
                        dispatch(setSelectedOpportunity(opportunityId));
                    });
                }
            });
    };

    const reject = () => {
        axios
            .post(
                `/api/Marketplace/MarketplaceOpportunities/${selectedOpportunityIdToReject}/Reject`,
                {
                    leaveComment: reasonForRejection,
                }
            )
            .then((response) => {
                if (response?.request?.status !== 200) {
                    createNotification(
                        'An Error occured during rejection.',
                        'error'
                    );
                } else {
                    fetchData(currentSort);
                }
            })
            .finally(() => {
                setReasonForRejection('');
            });
    };

    const tag = (opportunityId: number) => {
        axios
            .post(
                `/api/Marketplace/MarketplaceOpportunities/${opportunityId}/tag`
            )
            .then((response) => {
                if (response?.request?.status !== 200) {
                    createNotification(
                        'An Error occured during tagging opportuinity.',
                        'error'
                    );
                } else {
                    fetchData(currentSort);
                }
            });
    };

    const untag = (opportunityId: number) => {
        axios
            .post(
                `/api/Marketplace/MarketplaceOpportunities/${opportunityId}/untag`
            )
            .then((response) => {
                if (response?.request?.status !== 200) {
                    createNotification(
                        'An Error occured during untagging opportuinity.',
                        'error'
                    );
                } else {
                    fetchData(currentSort);
                }
            });
    };

    const nestedFields = [
        {
            name: 'currentEvent',
            sortPath: 'currentEvent.stage',
            filterPath: 'currentEvent.stage',
        },
        {
            name: 'practices.stateOfRegistration',
            sortPath: 'practices.length',
            filterPath: 'practices.stateOfRegistration',
            operator: 'eq',
        },
        {
            name: 'practices.locationType',
            sortPath: 'practices.length',
            filterPath: 'practices.locationType',
            operator: 'eq',
        },
        {
            name: 'practices.practiceType',
            sortPath: 'practices.length',
            filterPath: 'practices.practiceType',
            operator: 'eq',
        },
    ];

    const locationFilterProps = selectFilter({
        placeholder: 'State',
        options: () => allStates.map((m) => ({ value: m.name, label: m.name })),
    }) as TableColumnFilterProps;

    const opportunityStatusFilterProps = selectFilter({
        options: MarketplaceOpportunityStatusOptions,
        placeholder: 'Status',
    }) as TableColumnFilterProps;

    const practiceTypeFilterProps = selectFilter({
        placeholder: 'Type',
        options: PracticeFocusOptionsShort,
    }) as TableColumnFilterProps;

    const practiceLocationTypeFilterProps = selectFilter({
        placeholder: 'Type',
        options: LocationTypeOptions,
    }) as TableColumnFilterProps;

    const practiceTypeHeaderTooltipContent = (
        <ul>
            {PracticeFocusOptionsMapping.map((option) => {
                const item = (
                    <li>{`${option.shortLabel} - ${option.fullName}`}</li>
                );
                return item;
            })}
        </ul>
    );

    const columns = [
        {
            dataField: 'opportunityId',
            text: 'Opp ID',
            headerClasses: 'opportunity-id-header',
            sort: true,
        },
        {
            dataField: 'practices.practiceType',
            text: 'Practice Type',
            headerClasses: 'practice-type-header',
            // filter: practiceTypeFilterProps,
            headerFormatter: (_column: any, colIndex: any, state: any) => {
                return (
                    <>
                        <div>
                            {'Practice Type'}
                            <InfoTooltip
                                idText="practice-type-tooltip"
                                text=""
                                html={ReactDOMServer.renderToString(
                                    practiceTypeHeaderTooltipContent
                                )}></InfoTooltip>
                        </div>
                    </>
                );
            },
            formatter: (_: any, row: MarketplaceOpportunity) => {
                const mappedPracticeType = row.practices
                    .map(
                        (practice) =>
                            PracticeFocusOptionsMapping.find(
                                (f) => f.id === practice.practiceType
                            )?.shortLabel ?? ''
                    )
                    .filter((f) => f);

                const cellContent = mappedPracticeType.join(', ');
                return cellContent;
            },
        },
        {
            dataField: 'engagementFteDvms',
            text: 'FTE DVM',
            headerClasses: 'fte-dvm-header',
            sort: true,
        },
        {
            dataField: 'practices.locationType',
            text: 'Loc. Type',
            headerClasses: 'loc-type-header',
            // filter: practiceLocationTypeFilterProps,
            formatter: (_: any, row: MarketplaceOpportunity) => {
                const mappedPracticeType = row.practices
                    .map(
                        (practice) =>
                            LocationTypes.find(
                                (f) => f.id === practice.locationType
                            )?.name ?? ''
                    )
                    .filter((f) => f);

                const cellContent = mappedPracticeType.length
                    ? mappedPracticeType.join(', ')
                    : 'NA';
                return cellContent;
            },
        },
        {
            dataField: 'practices.stateOfRegistration',
            text: 'State',
            headerClasses: 'location-header',
            sort: true,
            filter: locationFilterProps,
            formatter: (_: any, row: MarketplaceOpportunity) => {
                const practices = row?.practices;
                if (!practices?.length) {
                    return 'NA';
                }

                const statesOfRegistration = practices
                    .filter((practice) => practice.stateOfRegistration)
                    .map((practice) =>
                        stateToCodeMapper(practice.stateOfRegistration)
                    )
                    .join(', ');
                return statesOfRegistration.length
                    ? statesOfRegistration
                    : 'NA';
            },
        },
        {
            dataField: 'currentEvent',
            text: 'Current Stage',
            headerClasses: 'deal-stage-header',
            sort: true,
            formatter: (cellContent: TimelineEvent) => {
                return cellContent ? <div>{cellContent?.stageName}</div> : 'NA';
            },
        },
        {
            dataField: 'status',
            text: 'Status',
            headerClasses: 'status-header',
            sort: true,
            filter: opportunityStatusFilterProps,
            formatter: (cellContent: MarketplaceOpportunityStatus) => {
                return (
                    <div
                        style={{
                            color: MarketplaceOpportunityStatusList.find(
                                (x) => x.id == cellContent
                            ).color,
                            fontFamily: 'SegoeUI-Bold',
                        }}>
                        {
                            MarketplaceOpportunityStatusList.find(
                                (x) => x.id == cellContent
                            ).name
                        }
                    </div>
                );
            },
        },
        {
            dataField: 'opportunityId',
            text: 'Actions',
            headerClasses: 'actions-column-header',
            headerFormatter: () => (
                <div className="d-flex justify-content-between w-100">
                    <div className="w-100">Actions</div>
                </div>
            ),
            formatter: (cellContent: number, row: MarketplaceOpportunity) => {
                const isEngageButtonVisible =
                    row.isEngageAvailable &&
                    engagingAvailableStatuses.some((s) => s === row.status);

                const isTagButtonVisible =
                    row.isEngageAvailable &&
                    taggingAvailableStatuses.some((s) => s === row.status);

                const isRejectButtonVisible = rejectingAvailableStatuses.some(
                    (s) => s === row.status
                );

                return (
                    <div className="buttons-wrapper">
                        {isEngageButtonVisible ? (
                            <Button
                                className="btn btn-primary"
                                onClick={(event) => {
                                    engage(cellContent);
                                    event.stopPropagation();
                                }}>
                                {row.status ===
                                MarketplaceOpportunityStatus.RejectedByBuyer
                                    ? 'Re-Engage'
                                    : 'Engage'}
                            </Button>
                        ) : (
                            <> </>
                        )}

                        {isTagButtonVisible ? (
                            <Button
                                className="btn btn-primary"
                                onClick={(event) => {
                                    tag(cellContent);
                                    event.stopPropagation();
                                }}>
                                {'Tag'}
                            </Button>
                        ) : (
                            <></>
                        )}

                        {row.status === MarketplaceOpportunityStatus.Tagged ? (
                            <Button
                                className="btn btn-primary"
                                onClick={(event) => {
                                    untag(cellContent);
                                    event.stopPropagation();
                                }}>
                                {'Un-Tag'}
                            </Button>
                        ) : (
                            <></>
                        )}

                        {isRejectButtonVisible ? (
                            <Button
                                className="btn btn-primary"
                                onClick={(event) => {
                                    setSelectedOpportunityIdToReject(
                                        cellContent
                                    );
                                    setIsConfirmationVisible(true);
                                    setRejectedPracticeName(
                                        row.practices.find((x) => x)
                                            ?.practiceName
                                    );
                                    event.stopPropagation();
                                }}>
                                {'Reject'}
                            </Button>
                        ) : (
                            <> </>
                        )}
                    </div>
                );
            },
        },
    ];

    const renderMappingComponent = () => {
        const mapHeader = (
            <div className="mb-3 page-header">
                <div className="header-title">{'Opportunities'}</div>
            </div>
        );
        return (
            <LandscapeMapContainer
                markers={markers}
                currentZoom={currentZoom}
                setCurrentZoom={setCurrentZoom}
                selectedMarkers={selectedMarkers}
                setSelectedMarkers={setSelectedMarkers}
                isFullScreen={isFullScreen}
                setIsFullScreen={setIsFullScreen}
                setMarkers={setMarkers}
                markerVisibilityZoom={16}
                renderColorsDescription={renderColorsDescription}
                renderShapesDescription={renderShapesDescription}
                colMd={isFullScreen ? 12 : 6}
                mapHeader={mapHeader}
            />
        );
    };

    return (
        <div className="marketplace-opportunities-tab">
            <div>
                <div
                    className="opportunities-container"
                    style={{ flexDirection: isFullScreen ? 'column' : 'row' }}>
                    {renderMappingComponent()}
                    <Col
                        md={isFullScreen ? 12 : 6}
                        className="opportunities-table">
                        <BootstrapTable
                            bootstrap4
                            remote
                            keyField="opportunityId"
                            data={opportunities}
                            columns={columns}
                            defaultSorted={defaultSorted}
                            filter={filterFactory()}
                            headerClasses="header-vetrical-align-top"
                            pagination={paginationFactory({
                                page,
                                sizePerPage,
                                totalSize,
                            })}
                            onTableChange={handleTableChange}
                            noDataIndication="No Opportunities available"
                            selectRow={{
                                clickToSelect: true,
                                mode: 'radio',
                                selected: selectedOpportunity?.opportunityId
                                    ? [selectedOpportunity?.opportunityId]
                                    : [],
                                onSelect: (row: MarketplaceOpportunity) =>
                                    handleTableRowSelect(
                                        row.opportunityId,
                                        markers
                                    ),
                                classes: 'selected-row',
                                nonSelectableClasses: 'non-selectable-row',
                                hideSelectColumn: true,
                            }}
                        />
                    </Col>
                </div>
            </div>
            {isConfirmationVisible && (
                <div className="reject-confirmation-popup">
                    <ConfirmationDialogWithReason
                        isVisible={isConfirmationVisible}
                        setIsVisible={setIsConfirmationVisible}
                        action={reject}
                        reasonForAction={reasonForRejection}
                        setReasonForAction={(reason: string) => {
                            setReasonForRejection(reason);
                        }}
                        title={`You have elected to leave the process with ${rejectedPracticeName}. Any feedback you can provide regarding why you made this decision will be appreciated by the seller.`}
                        loader={false}></ConfirmationDialogWithReason>
                </div>
            )}
        </div>
    );
};

export default MarketplaceOpportunities;
